import { useEffect, useState } from "react";
import { auth, database, appCheck } from "./firebase";
import { useOnlineStatus } from "../contexts/OnlineStatusContext";
import { reloadForUpdate, shouldAutoReloadChunk } from "../utils/deployUpdate";

export default function ErrorFallback({
  error,
  errorInfo,
  resetErrorBoundary,
}) {
  const { isOnline } = useOnlineStatus();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isChunkUpdateError, setIsChunkUpdateError] = useState(false);

  // Helper function to detect if error is App Check related
  const isAppCheckError = (error) => {
    if (!error) return false;
    const msg = (error.message || error.toString()).toLowerCase();
    return (
      msg.includes("permission_denied") ||
      msg.includes("unauthorized") ||
      msg.includes("app check")
    );
  };

  // Helper function to detect if error is network-related
  const isNetworkError = (error) => {
    if (!error) return false;

    const errorMessage = error.message?.toLowerCase() || "";
    const errorString = error.toString()?.toLowerCase() || "";

    const networkKeywords = [
      "network",
      "offline",
      "failed to fetch",
      "networkerror",
      "connection",
      "timeout",
      "internet",
      "fetch failed",
      "load failed",
      "cors",
      "net::err",
    ];

    return networkKeywords.some(
      (keyword) =>
        errorMessage.includes(keyword) || errorString.includes(keyword),
    );
  };

  const isOfflineError = !isOnline || isNetworkError(error);
  const isSecurityError = isAppCheckError(error);

  useEffect(() => {
    // Check for ChunkLoadError (lazy loading failure due to new deployment)
    const isChunkError =
      error?.name === "ChunkLoadError" ||
      error?.message?.includes("Loading chunk") ||
      error?.message?.includes("Loading CSS chunk");

    setIsChunkUpdateError(isChunkError);

    if (isChunkError) {
      if (shouldAutoReloadChunk()) reloadForUpdate();
      return;
    }

    let MainUserUID = (auth && auth.currentUser && auth.currentUser.uid) || "";
    let dateValue = new Date()?.toString() || "";

    if (error) {
      // Enhanced error information collection
      const errorDetails = {
        Error: ((error && error.message && error.message) || "")?.slice(0, 300),
        ErrorStack: (error && error.stack && error.stack)?.slice(0, 500) || "",
        ErrorType: error.name || "Unknown Error",
        ErrorString: error.toString()?.slice(0, 200) || "",
        ComponentStack:
          (errorInfo && errorInfo.componentStack)?.slice(0, 400) || "",
        UserAgent: navigator.userAgent?.slice(0, 200) || "",
        URL: window.location.href || "",
        Timestamp: Date.now(),
        auth: MainUserUID || "",
        Date: dateValue,
      };

      // Log detailed error to console for debugging
      console.error("Enhanced Error Details:", {
        error,
        errorInfo,
        errorDetails,
        userAgent: navigator.userAgent,
        url: window.location.href,
      });

      // Send to Firebase with enhanced details
      database
        .ref("/clientsMessages/Errors/")
        .push(errorDetails, (errorhere) => {
          if (errorhere) {
            console.error("Failed to send error to database:", errorhere);
          } else {
            console.log("Error successfully sent to database");
          }
        });
    }
  }, [error, errorInfo]);

  const handleRetry = async () => {
    if (isChunkUpdateError) {
      reloadForUpdate();
      return;
    }

    if (isSecurityError && appCheck) {
      setIsRefreshing(true);
      try {
        // Force refresh the token
        await appCheck.getToken(true);
        console.log("App Check token verified successfully");
      } catch (err) {
        console.warn("Failed to refresh App Check token:", err);
      } finally {
        setIsRefreshing(false);
      }
    }

    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      // Fallback: reload the page
      window.location.reload();
    }
  };

  return (
    <div
      role="alert"
      style={{
        width: "100%",
        padding: "2rem",
        backgroundColor: isChunkUpdateError
          ? "#eff6ff"
          : isOfflineError
            ? "#fef3c7"
            : "#fee2e2",
        borderRadius: "8px",
        border: isChunkUpdateError
          ? "1px solid #93c5fd"
          : isOfflineError
            ? "1px solid #fbbf24"
            : "1px solid #fca5a5",
        margin: "1rem",
      }}
    >
      <div style={{ textAlign: "center" }}>
        {isChunkUpdateError ? (
          <>
            <h3
              style={{
                color: "#1d4ed8",
                marginBottom: "1rem",
                fontSize: "1.5rem",
                fontWeight: "600",
              }}
            >
              New version available
            </h3>
            <p
              style={{
                color: "#1e3a8a",
                marginBottom: "1.5rem",
                fontSize: "1rem",
              }}
            >
              We published an update. Please reload to continue using the latest
              version.
            </p>
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#dbeafe",
                borderRadius: "6px",
                marginBottom: "1.5rem",
                border: "1px solid #93c5fd",
              }}
            >
              <p style={{ margin: 0, color: "#1e40af", fontSize: "0.9rem" }}>
                This prevents version mismatch issues caused by cached files
                after deployment.
              </p>
            </div>
          </>
        ) : isOfflineError ? (
          <>
            <h3
              style={{
                color: "#d97706",
                marginBottom: "1rem",
                fontSize: "1.5rem",
                fontWeight: "600",
              }}
            >
              ⚠️ Connection Error
            </h3>
            <p
              style={{
                color: "#78350f",
                marginBottom: "1.5rem",
                fontSize: "1rem",
              }}
            >
              {!isOnline
                ? "You are currently offline. Please check your internet connection and try again."
                : "A network error occurred. This might be due to a temporary connection issue."}
            </p>
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#fef9c3",
                borderRadius: "6px",
                marginBottom: "1.5rem",
                border: "1px solid #fde047",
              }}
            >
              <p style={{ margin: 0, color: "#713f12", fontSize: "0.9rem" }}>
                <strong>💡 Tip:</strong> Check your network connection and click
                "Retry" when you're back online.
              </p>
            </div>
          </>
        ) : isSecurityError ? (
          <>
            <h3
              style={{
                color: "#d97706",
                marginBottom: "1rem",
                fontSize: "1.5rem",
                fontWeight: "600",
              }}
            >
              🛡️ Security Check Required
            </h3>
            <p
              style={{
                color: "#78350f",
                marginBottom: "1.5rem",
                fontSize: "1rem",
              }}
            >
              Your security session has expired due to inactivity. We blocked
              the connection to keep your data safe.
            </p>
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#fff3cd",
                borderRadius: "6px",
                marginBottom: "1.5rem",
                border: "1px solid #ffeeba",
              }}
            >
              <p style={{ margin: 0, color: "#856404", fontSize: "0.9rem" }}>
                <strong>Action Required:</strong> Click{" "}
                <strong>"Verify & Retry"</strong> below to refresh your secure
                connection.
              </p>
            </div>
          </>
        ) : (
          <>
            <h3
              style={{
                color: "#dc2626",
                marginBottom: "1rem",
                fontSize: "1.5rem",
                fontWeight: "600",
              }}
            >
              🚨 Application Error
            </h3>
            <p
              style={{
                color: "#7f1d1d",
                marginBottom: "1.5rem",
                fontSize: "1rem",
              }}
            >
              Sorry, something went wrong. Our team has been notified about this
              issue.
            </p>
          </>
        )}

        {process.env.NODE_ENV === "development" && error && (
          <details
            style={{
              textAlign: "left",
              marginBottom: "1.5rem",
              padding: "1rem",
              backgroundColor: "#fef2f2",
              borderRadius: "4px",
              border: "1px solid #fca5a5",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontWeight: "600",
                color: "#dc2626",
                marginBottom: "0.5rem",
              }}
            >
              🔍 Error Details (Development Mode)
            </summary>
            <pre
              style={{
                color: "#7f1d1d",
                fontSize: "0.875rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                marginTop: "0.5rem",
              }}
            >
              {error.message}
              {error.stack && `\n\nStack Trace:\n${error.stack}`}
              {errorInfo &&
                errorInfo.componentStack &&
                `\n\nComponent Stack:${errorInfo.componentStack}`}
            </pre>
          </details>
        )}

        <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
          <button
            onClick={handleRetry}
            disabled={isRefreshing}
            style={{
              backgroundColor: isRefreshing
                ? "#9ca3af"
                : isChunkUpdateError
                  ? "#1d4ed8"
                  : isSecurityError
                    ? "#d97706"
                    : "#dc2626",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: isRefreshing ? "wait" : "pointer",
              fontWeight: "600",
              transition: "background-color 0.2s",
              opacity: isRefreshing ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (!isRefreshing)
                e.target.style.backgroundColor = isSecurityError
                  ? "#b45309"
                  : isChunkUpdateError
                    ? "#1e40af"
                    : "#b91c1c";
            }}
            onMouseLeave={(e) => {
              if (!isRefreshing)
                e.target.style.backgroundColor = isChunkUpdateError
                  ? "#1d4ed8"
                  : isSecurityError
                    ? "#d97706"
                    : "#dc2626";
            }}
          >
            {isRefreshing
              ? "Verifying..."
              : isChunkUpdateError
                ? "Reload now"
                : isSecurityError
                  ? "🛡️ Verify & Retry"
                  : "🔄 Try Again"}
          </button>

          <button
            onClick={() => isChunkUpdateError ? reloadForUpdate({ home: true }) : (window.location.href = "/")}
            style={{
              backgroundColor: "#6b7280",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "0.75rem 1.5rem",
              fontSize: "1rem",
              cursor: "pointer",
              fontWeight: "600",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#4b5563")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#6b7280")}
          >
            🏠 Go Home
          </button>
        </div>

        <p
          style={{
            color: "#6b7280",
            fontSize: "0.875rem",
            marginTop: "1.5rem",
          }}
        >
          Error ID: {Date.now()} | Time: {new Date().toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}
